import { ReportDefinition } from '../models/report-definition';
import { ReportRowVm } from '../models/report-row-vm';
import {
   asNumberOrNull,
   asString,
   formatCurrency,
   formatDate
} from '../utils/report-formatting.utils';
import { getReportArray } from '../utils/report-json.utils';
import { getRealTyallsRowId } from '../utils/report-row-id.utils';

function getComplimentaryRoomRows(reportJson: any): any[] {
   return getReportArray(reportJson, 'ComplimentaryRooms');
}

function getRowId(row: any): string {
   return (
      getRealTyallsRowId(row) ||
      `comp:${asString(row?.ReservationId)}:${asString(
         row?.ConfirmationNumber
      )}:${asString(row?.RoomNumber)}`
   );
}

function getGuestNameWithComments(row: any): string {
   const name = asString(row?.Name);
   const comments = asString(row?.Comments);

   if (!comments.trim()) {
      return name;
   }

   return `${name}\n${comments}`;
}

function getCompany(row: any): string {
   return asString(row?.CompCompanyAgentGroup) || asString(row?.RoutedTo) || '-';
}

function getBalance(row: any): number | null {
   return asNumberOrNull(row?.Balance);
}

function flattenRows(reportJson: any): ReportRowVm[] {
   return [...getComplimentaryRoomRows(reportJson)]
      .sort((a, b) => {
         return (
            asString(a?.RoomNumber).localeCompare(asString(b?.RoomNumber)) ||
            asString(a?.Name).localeCompare(asString(b?.Name))
         );
      })
      .map((row) => {
         const balance = getBalance(row);

         return {
            rowId: getRowId(row),
            cells: {
               rowType: 'complimentaryRoom',

               room: asString(row?.RoomNumber),
               guestName: getGuestNameWithComments(row),
               guestNameRaw: asString(row?.Name),
               comments: asString(row?.Comments),

               arrivalDate: formatDate(row?.ArrivalDate),
               arrivalDateRaw: asString(row?.ArrivalDate),

               departureDate: formatDate(row?.DepartureDate),
               departureDateRaw: asString(row?.DepartureDate),

               company: getCompany(row),
               balance: formatCurrency(balance),
               balanceRaw: balance,

               rateCode: asString(row?.RateCode),

               roomType: asString(row?.RoomType),
               persons: asString(row?.Persons),
               paymentMethod: asString(row?.PaymentMethod),
               marketCode: asString(row?.MarketCode),
               blockCode: asString(row?.BlockCode)
            }
         };
      });
}

export const COMPLIMENTARY_ROOMS_DEFINITION: ReportDefinition = {
   reportType: 'ComplimentaryRooms',
   version: 2,
   title: 'Complimentary Rooms',

   sorting: {
      enabled: true,
      persist: true
   },

   header: {
      title: 'Complimentary Rooms'
   },

   rowChecks: {
      enabled: false
   },

   annotations: {
      enabled: true
   },

   columns: [
      {
         key: 'room',
         header: 'ROOM',
         width: '80px',
         sort: { type: 'string' }
      },
      {
         key: 'guestName',
         header: 'GUEST NAME',
         width: '280px',
         maxChars: 120,
         sort: {
            type: 'string',
            getValue: (row) => row.cells['guestNameRaw']
         }
      },
      {
         key: 'arrivalDate',
         header: 'ARR. DATE',
         width: '120px',
         sort: {
            type: 'date',
            getValue: (row) => row.cells['arrivalDateRaw']
         }
      },
      {
         key: 'departureDate',
         header: 'DEP. DATE',
         width: '120px',
         sort: {
            type: 'date',
            getValue: (row) => row.cells['departureDateRaw']
         }
      },
      {
         key: 'company',
         header: 'COMPANY',
         width: '260px',
         maxChars: 90,
         sort: { type: 'string' }
      },
      {
         key: 'balance',
         header: 'BALANCE',
         width: '120px',
         sort: {
            type: 'number',
            getValue: (row) => row.cells['balanceRaw'],
            defaultDirection: 'desc'
         }
      },
      {
         key: 'rateCode',
         header: 'RATE CODE',
         width: '120px',
         sort: { type: 'string' }
      }
   ],

   flattenRows,

   filter: {
      fields: [
         { key: 'room', label: 'Room', type: 'string' },
         {
            key: 'guestName',
            label: 'Guest Name',
            type: 'string',
            getValue: (row) => row.cells['guestNameRaw']
         },
         { key: 'comments', label: 'Comments', type: 'string' },
         { key: 'arrivalDate', label: 'Arrival Date', type: 'date' },
         { key: 'departureDate', label: 'Departure Date', type: 'date' },
         { key: 'company', label: 'Company', type: 'string' },
         { key: 'rateCode', label: 'Rate Code', type: 'string' },
         {
            key: 'balance',
            label: 'Balance',
            type: 'number',
            getValue: (row) => row.cells['balanceRaw']
         }
      ]
   }
};
